import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { ArrowRight, BookOpen, CalendarDays, Clock3, Headphones, Share2, Trash2 } from 'lucide-react';
import './StoryShelf.css';
import StoryShelf3D from './StoryShelf3D.js';

const COVER_PALETTES = [
  { cover: '#314a43', foil: '#e6c98c', page: '#f5ecd8' },
  { cover: '#70474b', foil: '#f1c7a7', page: '#f6e8db' },
  { cover: '#3d526d', foil: '#c8d8e7', page: '#e7eef2' },
  { cover: '#675342', foil: '#e3c48e', page: '#f4ead5' },
  { cover: '#4f426b', foil: '#d8c6eb', page: '#eee6f5' },
  { cover: '#315b61', foil: '#b8dfd8', page: '#e1f0eb' },
];

const getPalette = (story, index) => {
  const seed = String(story?.id || story?.title || index);
  const hash = Array.from(seed).reduce((total, character) => total + character.charCodeAt(0), 0);
  return COVER_PALETTES[hash % COVER_PALETTES.length];
};

const getWordCount = (story) => {
  const text = story?.story_content || story?.content || story?.pages?.map((page) => page.text || page.content || '').join(' ') || '';
  return text.trim().split(/\s+/).filter(Boolean).length;
};

const getPreviewText = (story) => {
  const text = story?.story_content || story?.content || story?.pages?.[0]?.text || story?.pages?.[0]?.content || '';
  const trimmed = text.trim().replace(/\s+/g, ' ');
  return trimmed.length > 150 ? `${trimmed.slice(0, 150)}…` : trimmed;
};

const formatDate = (value) => {
  if (!value) return 'Recently made';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Recently made'
    : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const getPageCount = (story) => (Array.isArray(story?.pages) ? story.pages.length : 0);

function StoryCover({ story, index, selected = false, onSelect }) {
  const palette = getPalette(story, index);
  const title = story?.title || 'Untitled Story';
  const style = {
    '--cover-color': palette.cover,
    '--cover-foil': palette.foil,
    '--page-color': palette.page,
    ...(story?.cover_image_url
      ? { '--cover-image': `url("${story.cover_image_url.replaceAll('"', '%22')}")` }
      : {}),
  };

  return (
    <button
      type="button"
      className={`story-shelf__book ${selected ? 'is-selected' : ''}`}
      style={style}
      onClick={onSelect}
      aria-label={`Inspect ${title}`}
      aria-pressed={selected}
    >
      <span className="story-shelf__book-pages" aria-hidden="true" />
      <span className="story-shelf__book-cover">
        <span className="story-shelf__book-frame" aria-hidden="true" />
        <span className="story-shelf__book-mark" aria-hidden="true">✦</span>
        <span className="story-shelf__book-label">Make It Mindful</span>
        <strong className="story-shelf__book-title">{title}</strong>
        <span className="story-shelf__book-rule" aria-hidden="true" />
        <span className="story-shelf__book-type">Storybook · {index + 1}</span>
      </span>
    </button>
  );
}

function StoryShelf({ stories, selectedStory, onSelect, onRead, onShare, onDelete }) {
  const selectedIndex = useMemo(
    () => Math.max(0, stories.findIndex((story) => story.id === selectedStory?.id)),
    [stories, selectedStory?.id]
  );
  const selectedPalette = getPalette(selectedStory, selectedIndex);
  const pageCount = getPageCount(selectedStory);
  const wordCount = getWordCount(selectedStory);
  const hasAudio = selectedStory?.audio_urls && Object.keys(selectedStory.audio_urls).length > 0;
  const isGenerating = selectedStory?.images_status === 'generating';
  const title = selectedStory?.title || 'Untitled Story';
  const [hasWebGL, setHasWebGL] = useState(false);

  if (!selectedStory) return null;

  return (
    <section className="story-shelf" aria-label="Your story shelf">
      <div className="story-shelf__masthead">
        <div>
          <p className="story-shelf__eyebrow">The family collection</p>
          <h2 className="story-shelf__heading">Made to be read together.</h2>
          <p className="story-shelf__intro">
            Pull a story from the shelf whenever you need a little more wonder in the room.
          </p>
        </div>
        <p className="story-shelf__count">{stories.length.toString().padStart(2, '0')} volumes</p>
      </div>

      <div className="story-shelf__scene">
        <div className="story-shelf__ambient-word" aria-hidden="true">Stories</div>
        <StoryShelf3D
          stories={stories}
          selectedStory={selectedStory}
          onSelect={onSelect}
          onReady={() => setHasWebGL(true)}
          onError={() => setHasWebGL(false)}
        />
        <div className={`story-shelf__books story-shelf__books--fallback ${hasWebGL ? 'is-hidden' : ''}`} role="list" aria-label="Saved stories">
          {stories.map((story, index) => (
            <div className="story-shelf__book-slot" role="listitem" key={story.id}>
              <StoryCover
                story={story}
                index={index}
                selected={story.id === selectedStory.id}
                onSelect={() => onSelect(story)}
              />
            </div>
          ))}
        </div>
        {!hasWebGL && <div className="story-shelf__ledge" aria-hidden="true">
          <span />
        </div>}
        <div className="story-shelf__3d-index" role="list" aria-label="Select a story volume">
          {stories.map((story, index) => (
            <button
              type="button"
              role="listitem"
              key={story.id}
              className={story.id === selectedStory.id ? 'is-selected' : ''}
              onClick={() => onSelect(story)}
              aria-label={`Select volume ${index + 1}: ${story.title || 'Untitled Story'}`}
              aria-pressed={story.id === selectedStory.id}
            >
              <span>{String(index + 1).padStart(2, '0')}</span>
              {story.title || 'Untitled Story'}
            </button>
          ))}
        </div>
      </div>

      <article className="story-shelf__detail" style={{ '--detail-accent': selectedPalette.cover }}>
        <div className="story-shelf__detail-cover" aria-hidden="true">
          <div className="story-shelf__detail-cover-inner">
            {selectedStory.cover_image_url ? (
              <img src={selectedStory.cover_image_url} alt="" />
            ) : (
              <>
                <span className="story-shelf__book-mark">✦</span>
                <strong>{title}</strong>
                <span>Make It Mindful</span>
              </>
            )}
          </div>
        </div>

        <div className="story-shelf__detail-copy">
          <p className="story-shelf__eyebrow">
            Volume {String(selectedIndex + 1).padStart(2, '0')}
            {isGenerating && <span className="story-shelf__status">Illustrating now</span>}
          </p>
          <h3>{title}</h3>
          <p className="story-shelf__detail-preview">
            {getPreviewText(selectedStory) || 'A new story is waiting between these covers.'}
          </p>
          <dl className="story-shelf__meta">
            <div><dt><CalendarDays size={14} /> Made</dt><dd>{formatDate(selectedStory.created_at)}</dd></div>
            <div><dt><Clock3 size={14} /> Read</dt><dd>{Math.max(1, Math.round(wordCount / 180))} min</dd></div>
            <div><dt><BookOpen size={14} /> Pages</dt><dd>{pageCount || 'Text'}</dd></div>
            <div><dt><Headphones size={14} /> Audio</dt><dd>{hasAudio ? 'Ready' : 'Not yet'}</dd></div>
          </dl>
          <div className="story-shelf__detail-actions">
            <RouterLink className="story-shelf__read-button" to={`/story/${selectedStory.id}`} onClick={onRead}>
              Read this story <ArrowRight size={16} />
            </RouterLink>
            <button type="button" className="story-shelf__icon-button" onClick={() => onShare(selectedStory)} aria-label={`Share ${title}`} title="Share">
              <Share2 size={17} />
            </button>
            <button type="button" className="story-shelf__icon-button is-danger" onClick={() => onDelete(selectedStory)} aria-label={`Delete ${title}`} title="Delete">
              <Trash2 size={17} />
            </button>
          </div>
        </div>
      </article>
    </section>
  );
}

export default StoryShelf;
